import 'swiper/css';

import Avo from 'analytics/Avo';
import { ReactComponent as BackArrow } from 'assets/icons/arrow-back.svg';
import { ReactComponent as OnboardingTitleIcon1 } from 'assets/icons/onboarding-icon-1.svg';
import { ReactComponent as OnboardingTitleIcon2 } from 'assets/icons/onboarding-icon-2.svg';
import { ReactComponent as OnboardingTitleIcon3 } from 'assets/icons/onboarding-icon-3.svg';
import OnboardingBanner1PNG from 'assets/images/onboarding/onboarding_step1.png';
import OnboardingBanner1 from 'assets/images/onboarding/onboarding_step1.webp';
import OnboardingBanner2PNG from 'assets/images/onboarding/onboarding_step2.png';
import OnboardingBanner2 from 'assets/images/onboarding/onboarding_step2.webp';
import OnboardingBanner3PNG from 'assets/images/onboarding/onboarding_step3.png';
import OnboardingBanner3 from 'assets/images/onboarding/onboarding_step3.webp';
import OnboardingBanner4 from 'assets/images/onboarding/onboarding_step4.webp';
import Button from 'components/atoms/Button';
import BottomSheet, { type BottomSheetProps } from 'components/molecules/BottomSheet';
import CustomPagination from 'components/molecules/CarouselIndicator';
import { BUTTON_NAMES } from 'constant/button-names';
import useImagePreloader from 'hooks/useImagePreloader';
import i18n from 'i18n/config';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { trackEvent } from 'wrappers/reporting';

const StyledContainer = styled.div``;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
`;

const StyledSwiperSlide = styled(SwiperSlide)`
  padding: 0px 20px 0px;
`;

const StyledTitle = styled.h5`
  font-weight: 300;
  font-size: 12px;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 17px;
  margin-bottom: 16px;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`;

const StyledBackButton = styled.button`
  style: none;
  background: ${({ theme }) => theme.components.onboardingBottomSheet.button.backgroundColor};
  border: ${({ theme }) =>
    `${theme.components.onboardingBottomSheet.button.border.width}px solid ${theme.components.onboardingBottomSheet.button.border.color}`};
  border-radius: 40px;
  height: 48px;
  padding: 0 25px;
`;

const StyledDescription = styled.p`
  color: ${({ theme }) => theme.components.onboardingBottomSheet.description.color};
  font-weight: 300;
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 7px;
`;

const StyledUL = styled.ul`
  padding-left: 18px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 0px 16px 20px;
`;

const StyledBoldText = styled.span`
  font-weight: 500;
`;

// fallback for webp images
// in case the browser does not support webp
const slides = new Set([
  {
    banner: OnboardingBanner1,
    bannerFallback: OnboardingBanner1PNG,
    title: i18n.t('onboardingBottomSheet.slides.0.title'),
    titleIcon: <OnboardingTitleIcon1 />,
    description: [i18n.t('onboardingBottomSheet.slides.0.description.0'), i18n.t('onboardingBottomSheet.slides.0.description.1')],
  },
  {
    banner: OnboardingBanner2,
    bannerFallback: OnboardingBanner2PNG,
    title: i18n.t('onboardingBottomSheet.slides.1.title'),
    titleIcon: <OnboardingTitleIcon2 />,
    description: [i18n.t('onboardingBottomSheet.slides.1.description.0'), i18n.t('onboardingBottomSheet.slides.1.description.1')],
  },
  {
    banner: OnboardingBanner3,
    bannerFallback: OnboardingBanner3PNG,
    title: i18n.t('onboardingBottomSheet.slides.2.title'),
    titleIcon: <OnboardingTitleIcon3 />,
    description: [i18n.t('onboardingBottomSheet.slides.2.description.0'), i18n.t('onboardingBottomSheet.slides.2.description.1')],
  },
]);

const TOTAL_SLIDES = 2;
export default function OnboardingBottomSheet({ ...props }: BottomSheetProps) {
  const [swiper, setSwiper] = useState<any>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { t } = useTranslation();
  const theme = useTheme();
  const viewedAllPages = useRef(false);

  useEffect(() => {
    Avo.onboardingBottomsheetView({
      screen: activeIndex.toString() /* string */,
    });
  }, [activeIndex]);

  const slideNext = () => {
    swiper.slideNext();
  };

  const slidePrev = () => {
    swiper.slidePrev();
  };

  const showBackButton = useMemo(() => activeIndex > 0, [activeIndex]);

  const handleIndexChange = (instance: any) => {
    if (instance.activeIndex === TOTAL_SLIDES) {
      viewedAllPages.current = true;
    }
    setActiveIndex(instance.activeIndex);
  };

  const ButtonText = useMemo(() => (activeIndex === TOTAL_SLIDES ? t('common.done') : t('common.next')), [activeIndex, t]);

  const buttonName = useMemo(() => (activeIndex === TOTAL_SLIDES ? BUTTON_NAMES.DONE : BUTTON_NAMES.NEXT), [activeIndex]);

  const handleContinueClick = () => {
    if (activeIndex === TOTAL_SLIDES) {
      swiper.slideTo(0);
      setActiveIndex(0);
      props?.onClose?.();
    } else {
      slideNext();
    }
  };

  useImagePreloader([OnboardingBanner1, OnboardingBanner2, OnboardingBanner3, OnboardingBanner4]);

  const handleOnClose = useCallback(() => {
    props.onClose?.();
    trackEvent('onboardingBottomSheetClosed', {
      viewedAllPages: `${viewedAllPages.current}`,
      pageNo: `${activeIndex}`,
    });
    Avo.closeOnboardingSheet({
      viewedAllPages: `${viewedAllPages.current}`,
      pageNo: `${activeIndex}`,
    });
  }, [activeIndex, props]);

  return (
    <BottomSheet hasNoSpacing {...props} onClose={handleOnClose} title="Getting started">
      <Swiper onSwiper={setSwiper} onActiveIndexChange={handleIndexChange} spaceBetween={0} slidesPerView={1}>
        {Array.from(slides).map(({ banner, bannerFallback, description, title, titleIcon }) => (
          <StyledSwiperSlide key={banner}>
            <StyledContainer>
              <picture>
                <source srcSet={banner} type="image/webp" />
                <source srcSet={bannerFallback} type="image/png" />
                <StyledImage src={banner} alt="oboarding-banner" />
              </picture>
              <StyledTitle>
                {titleIcon} {title}
              </StyledTitle>
              <StyledUL>
                {description.map((d) => (
                  <li key={d}>
                    <StyledDescription>
                      <Trans
                        components={{
                          bold: <StyledBoldText />,
                        }}
                      >
                        {d}
                      </Trans>
                    </StyledDescription>
                  </li>
                ))}
              </StyledUL>
            </StyledContainer>
          </StyledSwiperSlide>
        ))}
      </Swiper>
      {/*  */}
      <CustomPagination activeIndex={activeIndex} slides={slides.size} />
      <Footer className="flex">
        {showBackButton && (
          <StyledBackButton onClick={slidePrev}>
            <BackArrow />
          </StyledBackButton>
        )}
        <Button
          backgroundColor={theme.components.onboardingBottomSheet.button.mainBackgroundColor}
          textColor={theme.components.onboardingBottomSheet.button.textColor}
          onClick={handleContinueClick}
          buttonName={buttonName}
        >
          {ButtonText}
        </Button>
      </Footer>
    </BottomSheet>
  );
}
